import {
  InfoBlock,
  InfoDraft,
  InfoTemplateId,
} from "../../interfaces/ProductObject";

/**
 * Create/edit-flow rules for an Info listing, mirroring the backend limits
 * (ProductConstants.INFO_*) so the author gets a message here instead of a
 * VALIDATION_ERROR after tapping Publish. The server stays the authority.
 */
export const INFO_LIMITS = {
  title: 100,
  summary: 500,
  blocks: 30,
  heading: 120,
  text: 5000,
  minColumns: 2,
  maxColumns: 10,
  rows: 100,
  cell: 120,
  columnLabel: 60,
  footnote: 300,
} as const;

export const INFO_TEMPLATES: { id: InfoTemplateId; label: string; hint: string }[] = [
  { id: "article", label: "Article", hint: "Headings and paragraphs" },
  { id: "timetable", label: "Timetable", hint: "Times, routes, schedules" },
  { id: "blank", label: "Blank", hint: "Start from nothing" },
];

export function newInfoBlockId(): string {
  const raw =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  return `b${raw.replace(/[^a-zA-Z0-9]/g, "").slice(0, 10)}`;
}

export function newTextBlock(heading = ""): InfoBlock {
  return { id: newInfoBlockId(), type: "text", heading, body: "" };
}

export function newTableBlock(columns: string[] = ["Time", "Details"], rowCount = 3): InfoBlock {
  return {
    id: newInfoBlockId(),
    type: "table",
    heading: "",
    columns: [...columns],
    rows: Array.from({ length: rowCount }, () => columns.map(() => "")),
    footnote: "",
  };
}

/** Starter blocks for a template. Applied only onto an EMPTY page, never over the author's work. */
export function templateBlocks(template: InfoTemplateId): InfoBlock[] {
  switch (template) {
    case "article":
      return [newTextBlock("Overview"), newTextBlock("Good to know")];
    case "timetable":
      return [newTableBlock(["Time", "Destination", "Notes"], 4), newTextBlock("Notes")];
    default:
      return [];
  }
}

const TEMPLATE_HEADINGS = new Set(["overview", "good to know", "notes"]);

/** A heading the author typed themselves (a template's own placeholder headings don't count). */
const isAuthoredHeading = (heading: string | null | undefined): boolean => {
  const h = (heading ?? "").trim().toLowerCase();
  return h.length > 0 && !TEMPLATE_HEADINGS.has(h);
};

/**
 * True when the page holds nothing the author typed (template placeholders and
 * their default headings don't count), so picking another template may safely
 * swap the starter blocks without ever overwriting real work.
 */
export function isDraftBlank(blocks: InfoBlock[]): boolean {
  return blocks.every((b) =>
    b.type === "table"
      ? !isAuthoredHeading(b.heading) &&
        !(b.footnote ?? "").trim() &&
        (b.rows ?? []).every((r) => r.every((c) => !(c ?? "").trim()))
      : !isAuthoredHeading(b.heading) && !(b.body ?? "").trim()
  );
}

/** Tab- or comma-separated text (a copied spreadsheet range) → rows of cells. */
export function parsePastedRows(text: string, columnCount: number): string[][] {
  const lines = text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((l) => l.trim().length > 0);
  if (lines.length === 0) return [];
  const delimiter = lines.some((l) => l.includes("\t")) ? "\t" : ",";
  return lines.map((line) => {
    const cells = line.split(delimiter).map((c) => c.trim());
    const row = cells.slice(0, columnCount);
    while (row.length < columnCount) row.push("");
    return row;
  });
}

/**
 * What actually gets saved: untouched placeholders (empty text blocks, tables
 * with no filled row) are dropped, cells trimmed and rows padded to the column
 * count. Never mutates the draft.
 */
export function cleanBlocks(blocks: InfoBlock[]): InfoBlock[] {
  const out: InfoBlock[] = [];
  for (const block of blocks) {
    if (block.type === "text") {
      const body = (block.body ?? "").trim();
      if (!body) continue;
      out.push({ id: block.id, type: "text", heading: (block.heading ?? "").trim() || null, body });
    } else if (block.type === "table") {
      const columns = (block.columns ?? []).map((c) => (c ?? "").trim());
      const rows = (block.rows ?? [])
        .map((row) => columns.map((_, i) => (row[i] ?? "").trim()))
        .filter((row) => row.some((cell) => cell.length > 0));
      if (rows.length === 0) continue;
      out.push({
        id: block.id,
        type: "table",
        heading: (block.heading ?? "").trim() || null,
        columns,
        rows,
        footnote: (block.footnote ?? "").trim() || null,
      });
    }
  }
  return out;
}

/** First problem the author must fix before leaving the content step, or null. */
export function validateInfoContent(title: string, summary: string, blocks: InfoBlock[]): string | null {
  if (!title.trim()) return "Add a title";
  if (title.trim().length > INFO_LIMITS.title) return `Title can be at most ${INFO_LIMITS.title} characters`;
  if (!summary.trim()) return "Add a short summary";
  if (summary.trim().length > INFO_LIMITS.summary) return `Summary can be at most ${INFO_LIMITS.summary} characters`;

  const cleaned = cleanBlocks(blocks);
  if (cleaned.length === 0) return "Add some content: a text block or a table with at least one row";
  if (cleaned.length > INFO_LIMITS.blocks) return `A page can have at most ${INFO_LIMITS.blocks} blocks`;

  for (const [index, block] of cleaned.entries()) {
    const label = `Block ${index + 1}`;
    if ((block.heading ?? "").length > INFO_LIMITS.heading) return `${label}: heading is too long (max ${INFO_LIMITS.heading})`;
    if (block.type === "text") {
      if ((block.body ?? "").length > INFO_LIMITS.text) return `${label}: text is too long (max ${INFO_LIMITS.text} characters)`;
    } else {
      const cols = block.columns ?? [];
      if (cols.length < INFO_LIMITS.minColumns || cols.length > INFO_LIMITS.maxColumns)
        return `${label}: a table needs ${INFO_LIMITS.minColumns} to ${INFO_LIMITS.maxColumns} columns`;
      if (cols.some((c) => !c)) return `${label}: every column needs a label`;
      if ((block.rows ?? []).length > INFO_LIMITS.rows) return `${label}: at most ${INFO_LIMITS.rows} rows`;
      if ((block.rows ?? []).some((r) => r.some((c) => c.length > INFO_LIMITS.cell)))
        return `${label}: a cell is too long (max ${INFO_LIMITS.cell} characters)`;
      if ((block.footnote ?? "").length > INFO_LIMITS.footnote) return `${label}: footnote is too long (max ${INFO_LIMITS.footnote})`;
    }
  }
  return null;
}

/** The two request fields the Info listing adds to create/update. */
export function buildInfoRequestParts(draft: InfoDraft | undefined) {
  const d: InfoDraft = draft ?? { template: "blank", blocks: [], sponsor: null };
  return {
    infoContent: { schemaVersion: 1, blocks: cleanBlocks(d.blocks) },
    // Always sent: [] removes a sponsor on edit; on create it is simply empty.
    sponsors: d.sponsor
      ? [
          {
            slot: 1,
            kind: d.sponsor.kind,
            ...(d.sponsor.kind === "BUSINESS"
              ? { businessId: d.sponsor.businessId }
              : { userId: d.sponsor.userId }),
          },
        ]
      : [],
  };
}
